import Api from '@sendsprint/api-types';
import ClientApi from '@src/types/client';
import { useAccount } from '@src/contexts';
import { generateOtpService, verifyOtpService } from '@src/services/auth-service';
import { addTelCode } from '@src/utils/getCountryTelCode';
import { getState } from '@src/utils/stateStorage';
import { useCallback, useEffect, useState } from 'react';
import { SetAddressFormData } from '@src/pages/auth/register/types';

type GenerateOtpPayloadI = Omit<ClientApi.Account.GenerateOtp.GenerateOtpI, 'otp'>;
export type UpdateType = 'phoneUpdate' | 'setAddress';
export type StepsForPhoneUpdateI = 'initial' | 'generated' | 'verified';

interface UseAddressUpdateOptions {
  type: UpdateType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmitSuccess?: (value: any) => void;
  onVerifySuccess?: () => void;
  hash?: string;
  email?: string;
}

export interface UseAddressUpdateResponse {
  phoneUpdateLoading: boolean;
  verifyStatus: boolean | null;
  isPhoneNumberModalShown: boolean;
  isPhoneNumberSuccessModalShown: boolean;
  handleAddressUpdate: () => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleUserDetailsUpdate: (values: any, helpers: any) => Promise<void>;
  formState: SetAddressFormData | undefined;
  handleChangePhoneNo: () => void;
  handlePhoneNumberSuccessClose: () => void;
  stepsForPhoneUpdate: StepsForPhoneUpdateI;
  setIsPhoneNumberModalShown: React.Dispatch<React.SetStateAction<boolean>>;
  handlePhoneNumberClose: () => void;
  addressResponse: ClientApi.General.UserAddress | undefined;
}

/**
 * custom hook to handle address and phone number update
 * it also handles the otp logic
 */
const useAddressUpdate = ({
  onSubmitSuccess,
  type,
  hash,
  email,
  onVerifySuccess
}: UseAddressUpdateOptions): UseAddressUpdateResponse => {
  const [phoneUpdateLoading, setPhoneUpdateLoading] = useState(false);
  const [stepsForPhoneUpdate, setStepsForPhoneUpdate] = useState<StepsForPhoneUpdateI>('initial');
  const [verifyStatus, setVerifyStatus] = useState<boolean | null>(null);
  const [phoneNumberVal, setPhoneNumberVal] = useState('');
  const [isPhoneNumberModalShown, setIsPhoneNumberModalShown] = useState(false);
  const [isPhoneNumberSuccessModalShown, setIsPhoneNumberSuccessModalShown] = useState(false);
  const [formState, setFormState] = useState<SetAddressFormData>();
  const [addressResponse, setAddressResponse] = useState<ClientApi.General.UserAddress>();

  const { setAddress } = useAccount();
  const state = getState();

  const handlePhoneNumberClose = () => {
    setIsPhoneNumberModalShown(false);
    setStepsForPhoneUpdate('initial');
  };

  const handlePhoneNumberSuccessClose = () => {
    setIsPhoneNumberSuccessModalShown(false);
  };

  const handleGenerateOtp = async (payload: GenerateOtpPayloadI, values: SetAddressFormData) => {
    setPhoneUpdateLoading(true);
    try {
      const res = await generateOtpService({
        ...payload,
        hash: hash || payload.hash,
        email: email || payload.email
      });
      const addressValues = values as SetAddressFormData;

      if (res?.data?.ResponseCode === Api.Endpoint.Response.Code.Successful) {
        const countryCode = addressValues.country;

        setIsPhoneNumberModalShown(true);
        setPhoneNumberVal(addTelCode(countryCode, values.phone));
        setStepsForPhoneUpdate('generated');
      }

      if (type === 'setAddress') {
        setFormState({
          city: addressValues?.city || '',
          country: addressValues?.country as Api.Model.CountryInitials,
          phone: addTelCode(addressValues?.country, values.phone),
          postCode: addressValues?.postCode || '',
          streetAddress: addressValues?.streetAddress || '',
          state: addressValues?.state || '',
          ssn: addressValues?.ssn || '',
          dateOfBirth: addressValues?.dateOfBirth || '',
          percentage: addressValues?.percentage || '',
          role: addressValues?.role || ''
        });
      }

      setPhoneUpdateLoading(false);
    } catch (e) {
      setPhoneUpdateLoading(false);
    }
  };

  const handleVerifyOtp = async (payload: ClientApi.Account.GenerateOtp.GenerateOtpI) => {
    setPhoneUpdateLoading(true);
    try {
      const res = await verifyOtpService({
        ...payload,
        hash: hash || payload.hash,
        email: email || payload.email
      });

      if (res?.data?.ResponseCode === Api.Endpoint.Response.Code.Successful) {
        setStepsForPhoneUpdate('verified');
        setVerifyStatus(true);
      }

      if (res?.data?.ResponseCode === Api.Endpoint.Response.Code.DataNotFound) {
        setVerifyStatus(false);
      }

      setPhoneUpdateLoading(false);
    } catch (error) {
      setPhoneUpdateLoading(false);
    }
  };

  /**
   * handles generating otp and verifying otp
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUserDetailsUpdate = async (values: any, helpers: any) => {
    const hashVal = (state && state.tokens && state.tokens.hash) || hash || '';

    setPhoneUpdateLoading(true);
    if (stepsForPhoneUpdate === 'initial') {
      const countryCode = type === 'phoneUpdate' ? state?.address?.country : values?.country;

      const payload: GenerateOtpPayloadI = {
        email: email || values?.email || '',
        hash: hashVal,
        phone: addTelCode(countryCode, values.phone)
      };

      helpers.setTouched({
        otp: false
      });

      handleGenerateOtp(payload, values);
    } else if (stepsForPhoneUpdate === 'generated') {
      const countryCode = formState?.country as Api.Model.CountryInitials;

      const payload: ClientApi.Account.GenerateOtp.GenerateOtpI = {
        email: email || values?.email || '',
        hash: hashVal,
        phone: addTelCode(countryCode, values.phone),
        otp: values.otp || ''
      };

      handleVerifyOtp(payload);
    }
  };

  /**
   * handles the final address update
   */
  const handleAddressUpdate = useCallback(async () => {
    setPhoneUpdateLoading(true);
    const payload: Omit<SetAddressFormData, 'twilioTerms'> = {
      city: formState?.city || '',
      country: formState?.country as Api.Model.CountryInitials,
      phone: formState?.phone || phoneNumberVal,
      postCode: formState?.postCode || '',
      streetAddress: formState?.streetAddress || '',
      state: formState?.state || '',
      ssn: formState?.ssn || '',
      dateOfBirth: formState?.dateOfBirth || '',
      percentage: formState?.percentage || '',
      role: formState?.role || ''
    };

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await setAddress({ ...payload, hash, email } as any);

      if (res) {
        setAddressResponse(res);
      }

      handlePhoneNumberClose();
      setIsPhoneNumberSuccessModalShown(true);
      setPhoneUpdateLoading(false);
      if (onSubmitSuccess) {
        onSubmitSuccess(payload);
      }
    } catch (e) {
      setPhoneUpdateLoading(false);
    }
  }, [
    formState?.city,
    formState?.country,
    formState?.phone,
    formState?.postCode,
    formState?.streetAddress,
    phoneNumberVal,
    setAddress
  ]);

  /**
   * handles change phone number btn
   */
  const handleChangePhoneNo = () => {
    setStepsForPhoneUpdate('initial');
    setIsPhoneNumberModalShown(false);
    setVerifyStatus(null);
  };

  /**
   * effect that triggers the final address update
   */
  useEffect(() => {
    if (stepsForPhoneUpdate === 'verified') {
      if (onVerifySuccess) {
        onVerifySuccess();
      } else {
        handleAddressUpdate();
      }
    }
  }, [handleAddressUpdate, stepsForPhoneUpdate]);

  return {
    phoneUpdateLoading,
    verifyStatus,
    isPhoneNumberModalShown,
    isPhoneNumberSuccessModalShown,
    handleAddressUpdate,
    handleUserDetailsUpdate,
    formState,
    handleChangePhoneNo,
    handlePhoneNumberSuccessClose,
    stepsForPhoneUpdate,
    setIsPhoneNumberModalShown,
    handlePhoneNumberClose,
    addressResponse
  };
};

export default useAddressUpdate;
